import { measureGeneration } from '~/server/static-metrics'
import { GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { Dispatch, SetStateAction, useState } from 'react'
import { FaFileAlt } from 'react-icons/fa'
import InfiniteScroll from 'react-infinite-scroller'
import { BookList } from 'web_core/assets/book'
import { HeadProps } from 'web_core/assets/Head'
import { MathJax } from 'web_core/assets/mathdown/mathjax'
import { LoadingSpinner } from 'web_core/assets/spinners'
import { ESBook } from 'web_core/elastic_search'
import {
  Category,
  categoryConverter,
  categoryRef,
  fb,
  getDoc,
} from 'web_core/firebase'
import { useESBooks } from 'web_core/hooks'
import {
  deserialize,
  deserializeArray,
  serialize,
  Serialized,
} from 'web_core/utils'
import { addLocaleProps } from 'web_core/utils/static_props'
import { DefaultLayout } from '~/components/Layout/Default'
import { ContentNavSelector } from '~/components/Utils/ContentNavSelector'
import { Heading } from '~/components/Utils/Heading'
import {
  QueryOrder,
  QueryOrderSelector,
} from '~/components/Utils/QueryOrderSelector'
import { useLayoutProperty } from '~/context/layout_property'
import {
  CategoryPageParams,
  getCategoryPageStaticPaths,
} from '~/static_props/category'
import {
  getCategoriesStaticProps,
  getPopularProfilesStaticProps,
  LayoutStaticProps,
} from '~/static_props/layout'
import { bookCategoryBreadcrumb } from '~/utils'

type Props = {
  books: Serialized<ESBook>[]
  category: Serialized<Category>
} & LayoutStaticProps

export const getStaticPaths = getCategoryPageStaticPaths

export const getStaticProps: GetStaticProps<
  Props,
  CategoryPageParams
> = async ({ locale = 'ja', params }) =>
  measureGeneration('category_books', async (measure) => {
    if (!params) throw new Error('Missing params.')

    const { data } = await measure(() =>
      fb.call('search')({
        index: 'books',
        params: {
          type: 'query',
          category_ids: [params.id],
          order: 'score desc',
        },
      })
    )
    if (data.index !== 'books') {
      throw new Error('Unexpected response.')
    }

    const categorySnapshot = await measure(() => getDoc(categoryRef(params.id)))
    const category = categorySnapshot.data()
    if (!category) {
      throw new Error('category is not found.')
    }

    const result = {
      props: {
        books: data.result,
        category: serialize(category),
        categories: await measure(() => getCategoriesStaticProps()),
        popularProfiles: await measure(() => getPopularProfilesStaticProps()),
      },
      revalidate: 10,
    }
    return addLocaleProps(result, locale)
  })

type ContentsMenuProps = {
  category: Category
  order: QueryOrder
  setOrder: Dispatch<SetStateAction<QueryOrder>>
}

const ContentsMenu = ({ category, order, setOrder }: ContentsMenuProps) => {
  const { headerHeight } = useLayoutProperty()
  return (
    <div
      className="position-sticky bg-white pt-2 pb-2 mb-2 mb-md-4"
      style={{ top: headerHeight }}
    >
      <div className="d-flex justify-content-between gap-4">
        <ContentNavSelector
          variant="pills"
          active="books"
          link={`/categories/${category.id}/[contents]`}
          className="flex-shrink-0 fs-7 fs-md-6"
        />
        <QueryOrderSelector
          className="fs-7"
          order={order}
          onChange={setOrder}
          style={{ maxWidth: 300 }}
        />
      </div>
    </div>
  )
}

const Page: NextPage<Props> = ({
  category: staticCategory,
  books: staticBooks,
  categories,
  popularProfiles,
}) => {
  const { i18n } = useTranslation()
  const lang = i18n.language

  const category = deserialize<Category>(staticCategory, {
    ref: categoryConverter,
  })
  const [order, setOrder] = useState<QueryOrder>('score desc')
  const { books, loadMore, isReachingEnd, isLoadingMore } = useESBooks(
    {
      type: 'query',
      category_ids: [category.id],
      order,
    },
    deserializeArray(staticBooks, {})
  )

  const head: HeadProps = {
    title: category.localized_name[lang],
    description: category.localized_summary[lang],
    keyword: category.localized_name[lang],
  }

  return (
    <DefaultLayout
      head={head}
      navLinks={{ pc: true, tablet: true, sp: true, active: 'home' }}
      categoriesMenu={{ pc: true, sp: true, activeId: category.id }}
      staticCategories={categories}
      staticPopularProfiles={popularProfiles}
      leftSidebar
      rightSideBar
    >
      {bookCategoryBreadcrumb(category, lang).toReactNode()}
      <Heading
        title={category.localized_name[lang]}
        icon={FaFileAlt}
        className="mb-0"
      />
      {category.localized_summary[lang] && (
        <MathJax className="mt-2 mb-4">
          {category.localized_summary[lang]}
        </MathJax>
      )}
      <ContentsMenu category={category} order={order} setOrder={setOrder} />
      <InfiniteScroll
        loadMore={async () => {
          if (isLoadingMore === false && isReachingEnd === false)
            await loadMore()
        }}
        hasMore={isReachingEnd === false}
        loader={<LoadingSpinner />}
      >
        {books && <BookList books={books} />}
      </InfiniteScroll>
    </DefaultLayout>
  )
}

export default Page
